import { useNavigate } from "@tanstack/react-router";
import { Bell, Calendar, Lock, LogOut, type LucideIcon, User, UserX } from "lucide-react";
import type React from "react";
import { type ComponentProps, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { AppToggle } from "@/components/navigation/AppToggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BlockedUsersModal } from "@/features/user-setting/components/BlockedUsersModal";
import { EmailNotificationModal } from "@/features/user-setting/components/EmailNotificationModal";
import { PasswordModal } from "@/features/user-setting/components/PasswordModal";
import { useLogout } from "@/hooks/useLogout";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

const MenuButton = ({
  IconComponent,
  ...props
}: { IconComponent: LucideIcon } & ComponentProps<typeof Button>) => {
  return (
    <Button variant="ghost" size="icon" className="size-9 rounded-full bg-white" {...props}>
      <IconComponent />
    </Button>
  );
};

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useLogout();
  const userInfo = useGlobalUserInfo();

  // Modal states
  const [isEmailNotificationOpen, setIsEmailNotificationOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isBlockedUsersModalOpen, setIsBlockedUsersModalOpen] = useState(false);

  // Handle logout
  const handleLogout = async () => {
    await logout();
  };

  // Navigate to user profile
  const navigateToUserProfile = () => {
    if (userInfo?.id) {
      navigate({ to: "/user-profile/$user-id", params: { "user-id": String(userInfo.id) } });
    }
  };

  // Handle password modal
  const handlePasswordClick = () => {
    setIsPasswordModalOpen(true);
  };

  // Handle email notification modal
  const handleEmailNotificationClick = () => {
    setIsEmailNotificationOpen(true);
  };

  const handleBlockedUsers = () => {
    setIsBlockedUsersModalOpen(true);
  };

  return (
    <>
      <nav className="fixed top-0 right-0 left-0 z-50 h-[60px] bg-rw-sub">
        <div className="flex h-full flex-1 items-center justify-center px-4">
          {/* Left section */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-white.png" alt="logo" className="h-8" />
            </div>
          </div>

          {/* Spacer to push right content to the right */}
          <div className="flex-1" />

          {/* Right section */}
          <div className="flex items-center justify-center gap-3">
            <AppToggle />

            <MenuButton IconComponent={Calendar} onClick={() => navigate({ to: "/calendar" })} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-9 rounded-full bg-white p-0">
                  {userInfo?.display_name || userInfo?.image_url ? (
                    <CustomAvatar
                      name={userInfo?.display_name ?? t("common.profile")}
                      imageUrl={userInfo?.image_url}
                      size={36}
                      roundedFull
                    />
                  ) : (
                    <User />
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel>
                  <span className="block truncate text-gray-500">{userInfo?.display_name}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={navigateToUserProfile}>
                  <User className="size-4" />
                  {t("common.profile")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handlePasswordClick}>
                  <Lock className="size-4" />
                  {t("common.password")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEmailNotificationClick}>
                  <Bell className="size-4" />
                  {t("common.emailNotification")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleBlockedUsers}>
                  <UserX className="size-4" />
                  {t("common.blockedUsersOnMenu")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="size-4" />
                  {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Modals */}
      <PasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onLogout={logout}
      />
      <EmailNotificationModal
        isOpen={isEmailNotificationOpen}
        onClose={() => setIsEmailNotificationOpen(false)}
      />
      <BlockedUsersModal
        isOpen={isBlockedUsersModalOpen}
        onClose={() => setIsBlockedUsersModalOpen(false)}
      />
    </>
  );
};
